import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { fetchDepartments } from "../../../fetchers/cmmsAssetHierarchy";
import { replaceTradeDepartments } from "../../../fetchers/cmmsTrade";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getOutlinedButtonSx,
} from "../../cmms/assetHierarchyStyles";
import cmmsMessages from "../../cmms/cmmsMessages";
import FieldError from "../../cmmsSpareParts/components/FieldError";
import messages from "../messages";
import { toTradeDepartmentOptions } from "../tradeDepartmentOptions";

/*
 * Which departments a trade serves (AB#1624): a work order in one of them is sent to the trade's
 * members instead of to the department's.
 *
 * TradeMembersDialog with departments in place of people, for the same reasons: the endpoint is a
 * replace-set, so the field is the mapping after Save, and it is a dialog of its own because it is
 * a call of its own.
 *
 * The departments are read with inactive rows included and its own query key, rather than the
 * ["cmmsDepartments", companyKey] entry user administration reads active-only. The inactive rows
 * give a nested department its whole path and a deactivated mapping its name, and sharing a key
 * between two different answers would hand one of them the other's.
 */
export default function TradeDepartmentsDialog({ open, onClose, trade, companyKey }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const queryClient = useQueryClient();

  const [selectedIds, setSelectedIds] = useState([]);

  const departmentsQuery = useQuery(
    ["cmmsDepartments", companyKey, "includeInactive"],
    () => fetchDepartments(companyKey, true),
    { enabled: open && Boolean(companyKey), staleTime: 300000 },
  );

  // Seeded each time it opens, from the row it was opened on, and not kept in step with later
  // refetches — the same rule as the members dialog.
  useEffect(() => {
    if (!open) return;
    setSelectedIds((trade?.departments ?? []).map((department) => department.assetNodeId));
  }, [open, trade]);

  const options = useMemo(
    () => toTradeDepartmentOptions(departmentsQuery.data, selectedIds, trade?.departments),
    [departmentsQuery.data, selectedIds, trade],
  );

  // In the order they were picked, as with the members.
  const selectedOptions = useMemo(
    () => selectedIds
      .map((id) => options.find((option) => option.id === id))
      .filter(Boolean),
    [selectedIds, options],
  );

  const staleNames = selectedOptions
    .filter((option) => !option.isActive)
    .map((option) => option.title);

  const saveMutation = useMutation({
    mutationFn: (assetNodeIds) => replaceTradeDepartments(companyKey, trade.id, assetNodeIds),
  });

  const handleSubmit = async () => {
    try {
      await saveMutation.mutateAsync(selectedIds);
      await queryClient.invalidateQueries({ queryKey: ["cmmsTrades"] });
      pushNotification(intl.formatMessage(messages.tradeToastDepartmentsUpdated));
      onClose?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.tradeDepartmentsSaveFailed)));
    }
  };

  const submitting = saveMutation.isLoading;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(messages.tradeDepartmentsTitle, { name: trade?.name ?? "" })}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        {/*
          * Saving still works on a retired trade, as renaming and the members do. What has to be
          * said is that the mapping does nothing until the trade is back.
          */}
        {trade && !trade.isActive &&
          <Typography variant="body2" sx={{ color: "warning.main" }}>
            <FormattedMessage {...messages.tradeDepartmentsInactiveTradeHint} />
          </Typography>
        }
        <Box>
          <AxionControls.AxionAutocomplete
            name="tradeDepartments"
            label={intl.formatMessage(messages.tradeDepartmentsFieldLabel)}
            multiple
            options={options}
            value={selectedOptions}
            onChange={(_event, picked) => setSelectedIds((picked ?? []).map((option) => option.id))}
            disabled={departmentsQuery.isLoading || submitting}
          />
          {departmentsQuery.isError &&
            <FieldError message={intl.formatMessage(messages.tradeDepartmentsLoadFailed)} />
          }
          {staleNames.length > 0 &&
            <Typography variant="caption" component="p" sx={{ color: "warning.main", mt: 0.5 }}>
              <FormattedMessage {...messages.tradeDepartmentsStaleHint} values={{ names: staleNames.join(", ") }} />
            </Typography>
          }
          <Typography variant="caption" component="p" sx={{ color: "text.secondary", mt: 0.5 }}>
            <FormattedMessage {...messages.tradeDepartmentsFieldHint} />
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !trade?.id}
            sx={getContainedButtonSx(cmmsColors)}
          >
            <FormattedMessage {...cmmsMessages.save} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
